import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import ScenarioLayout from './components/ScenarioLayout';
import ScenarioReport from './components/ScenarioReport';
import AIInsights from './components/AIInsights';

import { yupResolver } from '@hookform/resolvers/yup';
import {
  createLedLightingSchema,
  createRooftopSolarSchema,
  createHvacSchema,
  createTemperatureReductionSchema,
  createGreenTariffPpaSchema,
  createFleetElectrificationSchema,
} from './validateSchema';

import { getScenarioDefinitions, SCENARIO_FORM_MAP } from './scenarioConfig';
import { getCurrentReportPeriod, getReportRangePeriod } from '../reports/reportHelper';
import { generateScenarioOutputApi, getScenarioBaselineApi } from './scenarioApi';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { parseUserInfo } from '../../utilities/common';
import SaveScenarioModal from './components/SaveScenarioModal';

const ScenarioPage = () => {
  const { scenarioKey } = useParams();
  const [baseline, setBaseline] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [extraParams, setExtraParams] = useState({});
  const [showSave, setShowSave] = useState(false);
  const { t } = useTranslation();

  const scenarios = useMemo(() => getScenarioDefinitions(t), [t]);
  const config = scenarios[scenarioKey];

  const { orgId, usrTimezone, ...userInfo } = parseUserInfo(useSelector((state) => state.users.user));
  const currentReportPeriod = getCurrentReportPeriod(userInfo.usrOrgOptions);
  const reportRangePeriod = getReportRangePeriod(userInfo.usrOrgOptions, currentReportPeriod.reportYear);

  const schemaMap = {
    led: createLedLightingSchema(t),
    solar: createRooftopSolarSchema(t),
    hvac: createHvacSchema(t),
    temp: createTemperatureReductionSchema(t),
    ppa: createGreenTariffPpaSchema(t),
    fleet: createFleetElectrificationSchema(t),
  };

  const loadModelBaseline = useCallback((params) => {
    setIsLoading(true);
    setExtraParams(prev => {
      const same = JSON.stringify(prev) === JSON.stringify(params);
      return same ? prev : params;
    });
    getScenarioBaselineApi(orgId, {
      model: scenarioKey,
      startDate: reportRangePeriod.startDate,
      endDate: reportRangePeriod.endDate,
      usrTimezone,
      ...params,
    })
      .then(res => {
        setIsLoading(false);
        setBaseline(res);
      })
      .catch(() => {
        setIsLoading(false);
        toast.error(t('Failed to load CarbonView data for this scenario.'));
      })
  }, [
    scenarioKey,
    orgId,
    reportRangePeriod.startDate,
    reportRangePeriod.endDate,
    usrTimezone,
    t,
  ]);

  useEffect(() => {
    if (
      !scenarioKey
      || !reportRangePeriod.startDate
      || scenarioKey === baseline?.model
    ) return;
    loadModelBaseline({});
  }, [
    scenarioKey,
    baseline?.model,
    reportRangePeriod.startDate,
    reportRangePeriod.endDate,
    loadModelBaseline,
  ]);

  const form = useForm({
    mode: 'onChange',
    resolver: yupResolver(schemaMap[scenarioKey]),
  });
  const { handleSubmit } = form;

  const [result, setResult] = useState(null);

  const FormComponent = SCENARIO_FORM_MAP[scenarioKey];

  if (!FormComponent || !config) {
    return <div>{t('Scenario not supported')}</div>;
  }

  const onGenerate = async (values) => {
    try {
      setIsLoading(true);
      const payload = {
        model: scenarioKey,
        startDate: reportRangePeriod.startDate,
        endDate: reportRangePeriod.endDate,
        usrTimezone,
        inputs: values,
        ...extraParams,
      };
      const calculated = await generateScenarioOutputApi(orgId, payload);
      setResult(calculated);
      if (calculated?.baseline && calculated?.model === scenarioKey) {
        setBaseline(prev => ({
          ...(prev ?? {}),
          model: calculated.model,
          data: calculated.baseline,
        }));
      }
    } catch (e) {
      toast.error(t('Failed to generate output for this scenario.'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerate = handleSubmit(onGenerate);

  return (
    <ScenarioLayout
      title={config.title}
      subtitle={config.subtitle}
      icon={config.icon}
      color={config.color}
      onGenerate={handleGenerate}
      onSave={() => setShowSave(true)}
      disableSave={!result}
      isLoading={isLoading}
    >
      <FormProvider {...form}>
        <FormComponent
          isLoading={isLoading}
          baseline={baseline?.model === scenarioKey ? baseline : null}
          loadModelBaseline={loadModelBaseline}
          setResult={setResult}
        />
      </FormProvider>

      {result && (
        <>
          <ScenarioReport
            result={result?.model === scenarioKey ? result : null}
          />
          <AIInsights insights={result.aiInsights} />
        </>
      )}
      <SaveScenarioModal
        show={showSave}
        onClose={() => setShowSave(false)}
        orgId={orgId}
        model={scenarioKey}
        handleSubmit={handleSubmit}
        inputs={{
          startDate: reportRangePeriod.startDate,
          endDate: reportRangePeriod.endDate,
          usrTimezone,
        }}
        baseline={baseline}
        result={result}
      />
    </ScenarioLayout>
  );
};

export default ScenarioPage;
